import { useMemo } from 'react';

import { useSubscriptionPricesQuery } from '@/store/subscriptions/subscriptions.store';
import type {
  CreateCheckoutSubscription,
  PaidSubscriptionTier,
} from '@/store/subscriptions/subscriptions.types';

import { MIN_CREW_SEATS } from './subscription.display';
import {
  crewLookupKey,
  fmtPrice,
  planBill,
  quoteLookupKeys,
  seatLookupKey,
  unitPrice,
} from './subscription.pricing';

/**
 * What the plan the form currently holds costs. Every amount is formatted or `null` —
 * `null` while the prices are in flight and whenever one the quote needs is missing, which
 * is what keeps a made-up figure off the screen.
 */
export interface PlanQuote {
  /** One seat on a tier, e.g. `"$19.99"` — stated per tier so the radios can be priced. */
  seat: (tier: PaidSubscriptionTier) => string | null;
  /** One crew seat. Priced per interval only, so it does not depend on the tier. */
  crew: string | null;
  /** The whole draft — seats and crew together — for one billing period. */
  total: string | null;
}

/**
 * Prices the draft against `GET /subscriptions/prices`. Both tiers and the crew add-on are
 * fetched at once, so stepping seats or switching tier re-prices from cache.
 *
 * The vessel is its own service, so its id satisfies the `X-Service-Id` header directly.
 */
export const usePlanQuote = (serviceId: string, input: CreateCheckoutSubscription): PlanQuote => {
  const lookupKeys = useMemo(() => quoteLookupKeys(input.interval), [input.interval]);
  const { data: prices } = useSubscriptionPricesQuery(serviceId, lookupKeys);

  return useMemo<PlanQuote>(() => {
    const formatted = (key: string) => {
      const price = unitPrice(prices, key);
      return price && !Number.isNaN(price.amount) ? fmtPrice(price.amount, price.currency) : null;
    };

    const bill = planBill({ ...input, crewSeats: input.crewSeats ?? MIN_CREW_SEATS }, prices);

    return {
      seat: (tier) => formatted(seatLookupKey(tier, input.interval)),
      crew: formatted(crewLookupKey(input.interval)),
      total: bill && fmtPrice(bill.amount, bill.currency),
    };
  }, [input, prices]);
};
